import React, { ChangeEvent, createContext, ReactNode, useCallback, useMemo, useState } from "react";
import { getDiseaseFromValue } from "../../domain/assessment/Disease";
import { FamilyDistance, getFamilyDistanceFromValue } from "../../domain/assessment/FamilyDistance";
import { Height } from "../../domain/assessment/Height";
import { getLifeStatusFromValue, LifeStatus } from "../../domain/assessment/LifeStatus";
import { getNursingDegreeFromValue, NursingDegree } from "../../domain/assessment/NursingDegree";
import { OtherDiseases } from "../../domain/assessment/OtherDiseases";
import { Weight } from "../../domain/assessment/Weight";
import { useAppDispatch } from "../../state/store";
import { updateAssessmentInfoAction } from "../../state/usecase/assessment/info/actions";
import { isPositiveNumber } from "../../utility/isPositiveNumber";
import { useAssessmentInfo } from "../hooks/assessment/info/useAssessmentInfo";
import { useCareRecipientIdFromPath } from "../hooks/care_recipient/useCareRecipientIdFromPath";
import { useInput } from "../hooks/common/useInput";

type CareRecipientDetailContextType = {
    height: string
    handleHeightChanged: (height: string) => void
    weight: string
    handleWeightChanged: (height: string) => void
    weekdayLifeStatus: string
    handleWeekdayLifeStatusChanged: (weekdayLifeStatus: string) => void
    holidayLifeStatus: string
    handleHolidayLifeStatusChanged: (holidayLifeStatus: string) => void
    familyDistance: string
    handleFamilyDistanceChanged: (familyDistance: string) => void
    bathAtHome: string
    handleBathAtHomeStatusChanged: (bathAtHome: string) => void
};

const defaultCareRecipientDetailContextValue: CareRecipientDetailContextType = {
    height: "",
    handleHeightChanged: (_height: string) => {},
    weight: "",
    handleWeightChanged: (_weight: string) => {},
    weekdayLifeStatus: "",
    handleWeekdayLifeStatusChanged: (_weekdayLifeStatus: string) => {},
    holidayLifeStatus: "",
    handleHolidayLifeStatusChanged: (_holidayLifeStatus: string) => {},
    familyDistance: "",
    handleFamilyDistanceChanged: (_familyDistance: string) => {},
    bathAtHome: "",
    handleBathAtHomeStatusChanged: (_bathAtHome: string) => {},
};

type CareRecipientStatusContextType = {
    nursingDegree: string
    handleNursingDegreeChanged: (nursingDegree: string) => void
    diseases: string[]
    handleSufferedDiseaseChanged: (e: ChangeEvent<HTMLInputElement>) => void
    otherDiseases: string
    handleOtherDiseasesChanged: (otherDiseases: string) => void
    diuretic: string
    handleDiureticChanged: (diuretic: string) => void
};

const defaultCareRecipientStatusContextValue: CareRecipientStatusContextType = {
    nursingDegree: "",
    handleNursingDegreeChanged: (_nursingDegree: string) => {},
    diseases: [],
    handleSufferedDiseaseChanged: (_e: ChangeEvent<HTMLInputElement>) => {},
    otherDiseases: "",
    handleOtherDiseasesChanged: (_otherDiseases: string) => {},
    diuretic: "",
    handleDiureticChanged: (_diuretic: string) => {}
};

type ContextType = CareRecipientDetailContextType & CareRecipientStatusContextType & {
    handleAssessmentUpdateClicked: () => void
};

const defaultContextValue: ContextType = {
    ...defaultCareRecipientDetailContextValue,
    ...defaultCareRecipientStatusContextValue,
    handleAssessmentUpdateClicked: () => {}
};

export const AssessmentInfoContext = createContext(defaultContextValue);

export const AssessmentInfoProvider = React.memo(function AssessmentInfoProvider(props: Props) {
    const dispatch = useAppDispatch();

    const careRecipientId = useCareRecipientIdFromPath();

    const {
        careRecipientDetail,
        sufferedDisease: sufferedDiseaseArray,
        otherSufferedDiseases,
        dosingMedicine
    } = useAssessmentInfo();

    // 基本情報のstate
    const [height, , handleHeightChanged] = useInput(careRecipientDetail.height.value);
    const [weight, , handleWeightChanged] = useInput(careRecipientDetail.weight.value);
    const [weekdayLifeStatus, , handleWeekdayLifeStatusChanged] = useInput(careRecipientDetail.weekdayLifeStatus ? careRecipientDetail.weekdayLifeStatus.value : LifeStatus.fromNothing().value);
    const [holidayLifeStatus, , handleHolidayLifeStatusChanged] = useInput(careRecipientDetail.holidayLifeStatus ? careRecipientDetail.holidayLifeStatus.value : LifeStatus.fromNothing().value);
    const [bathAtHome, , handleBathAtHomeChanged] = useInput(careRecipientDetail.bathAtHome? careRecipientDetail.bathAtHome.value : "true");
    const [familyDistance, , handleFamilyDistanceChanged] = useInput(careRecipientDetail.familyDistance ? careRecipientDetail.familyDistance.value : FamilyDistance.fromNothing().value);

    // 介護状況のstate
    const [nursingDegree, , handleNursingDegreeChanged] = useInput(careRecipientDetail.nursingDegree ? careRecipientDetail.nursingDegree.value : NursingDegree.fromNothing().value);
    const [diseases, setDiseases] = useState<string[]>(sufferedDiseaseArray ? sufferedDiseaseArray.map(disease => disease === undefined ? "" : disease.value) : []);
    const [otherDiseases, , handleOtherDiseasesChanged] = useInput(otherSufferedDiseases.value);
    const [diuretic, , handleDiureticChanged] = useInput(dosingMedicine.value);
    const handleSufferedDiseaseChanged = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setDiseases(prevList =>
            prevList.includes(e.target.value) ? prevList.filter(disease => disease !== e.target.value) : [...prevList, e.target.value]
        );
    }, []);

    // 入力値のバリデーション・保存
    const handleAssessmentUpdateClicked = useCallback(() => {
        // フォーマットチェック
        const invalidMessage: string[] = [];
        if (!isPositiveNumber(Number(height)) && height.length > 0) {
            invalidMessage.push("「身長」は正数で入力してください。");
        }
        if (!isPositiveNumber(Number(weight)) && weight.length > 0) {
            invalidMessage.push("「体重」は正数で入力してください。");
        }
        if (invalidMessage.length) {
            alert(invalidMessage.join("\n"));
            return;
        }

        if (!careRecipientId) return;

        dispatch(
            updateAssessmentInfoAction({
                careRecipientId: careRecipientId,
                height: Height.from(height),
                weight: Weight.from(weight),
                weekdayLifeStatus: getLifeStatusFromValue(weekdayLifeStatus),
                holidayLifeStatus: getLifeStatusFromValue(holidayLifeStatus),
                familyDistance: getFamilyDistanceFromValue(familyDistance),
                nursingDegree: getNursingDegreeFromValue(nursingDegree),
                sufferedDisease: diseases.map(disease => getDiseaseFromValue(disease)),
                otherSufferedDiseases: OtherDiseases.fromString(otherDiseases),
                diuretic: diuretic === "true",
                bathAtHome: bathAtHome === "true",
            })
        );
    }, [dispatch, careRecipientId, height, weight, weekdayLifeStatus, holidayLifeStatus, familyDistance, nursingDegree, diseases, otherDiseases, diuretic, bathAtHome]);

    const careRecipientDetailContextValue: CareRecipientDetailContextType = useMemo(() => ({
        height: height,
        handleHeightChanged: handleHeightChanged,
        weight: weight,
        handleWeightChanged: handleWeightChanged,
        weekdayLifeStatus: weekdayLifeStatus,
        handleWeekdayLifeStatusChanged: handleWeekdayLifeStatusChanged,
        holidayLifeStatus: holidayLifeStatus,
        handleHolidayLifeStatusChanged: handleHolidayLifeStatusChanged,
        bathAtHome: bathAtHome,
        handleBathAtHomeStatusChanged: handleBathAtHomeChanged,
        familyDistance: familyDistance,
        handleFamilyDistanceChanged: handleFamilyDistanceChanged
    }), [
        height,
        handleHeightChanged,
        weight,
        handleWeightChanged,
        weekdayLifeStatus,
        handleWeekdayLifeStatusChanged,
        holidayLifeStatus,
        handleHolidayLifeStatusChanged,
        familyDistance,
        handleFamilyDistanceChanged,
        bathAtHome,
        handleBathAtHomeChanged
    ]);

    const careRecipientStatusContextValue: CareRecipientStatusContextType = useMemo(() => ({
        nursingDegree: nursingDegree,
        handleNursingDegreeChanged: handleNursingDegreeChanged,
        diseases: diseases,
        handleSufferedDiseaseChanged: handleSufferedDiseaseChanged,
        otherDiseases: otherDiseases,
        handleOtherDiseasesChanged: handleOtherDiseasesChanged,
        diuretic: diuretic,
        handleDiureticChanged: handleDiureticChanged
    }), [
        nursingDegree,
        handleNursingDegreeChanged,
        diseases,
        handleSufferedDiseaseChanged,
        otherDiseases,
        handleOtherDiseasesChanged,
        diuretic,
        handleDiureticChanged
    ]);

    const value: ContextType = useMemo(() => ({
        ...careRecipientDetailContextValue,
        ...careRecipientStatusContextValue,
        handleAssessmentUpdateClicked: handleAssessmentUpdateClicked
    }), [careRecipientDetailContextValue, careRecipientStatusContextValue, handleAssessmentUpdateClicked]);

    return (
        <AssessmentInfoContext.Provider value={value}>{props.children}</AssessmentInfoContext.Provider>
    );
});

type Props = {
    children: ReactNode
};
